/**
 * Calculates pixel widths for table columns based on container width and column configurations.
 *
 * This function performs a three-pass calculation:
 * 1. First pass: Processes fixed-width columns (those with `size` but no `initialPercentWidth`)
 *    and calculates the total space they consume.
 * 2. Second pass: Allocates remaining space to percentage-based columns
 *    (those with `initialPercentWidth`), clamping each to its min/max.
 * 3. Third pass: Redistributes any leftover width into the columns that still
 *    have headroom, so maxSize-capped columns don't leave the table short of
 *    the container.
 *
 * @param containerWidth - The total available width in pixels for the table container
 * @param columns - Array of column configuration objects
 * @param columns[].id - Unique identifier for the column
 * @param columns[].size - Fixed width in pixels (used for fixed-width columns)
 * @param columns[].initialPercentWidth - Percentage of available width (0-100) for percentage-based columns
 * @param columns[].minSize - Minimum width in pixels (optional constraint)
 * @param columns[].maxSize - Maximum width in pixels (optional constraint)
 * @param fillRemainingSpace - When true, leftover width from capped columns is
 *   pushed into uncapped columns so the table fills its container
 *
 * @returns Object mapping column IDs to their calculated pixel widths
 *
 * @example
 * ```typescript
 * // Simple case — percentages sum to 100, no clamping
 * calculateColumnWidths(1200, [
 *   { id: 'actions', size: 80 },                              // fixed 80px
 *   { id: 'name', initialPercentWidth: 60, minSize: 300 },    // 60% of 1120px
 *   { id: 'date', initialPercentWidth: 40, minSize: 200 },    // 40% of 1120px
 * ]);
 * // → { actions: 80, name: 672, date: 448 }
 *
 * // Redistribution — `actions` is capped at maxSize 50, so its
 * // unused share is pushed back into `name` and `date` proportionally.
 * calculateColumnWidths(1500, [
 *   { id: 'actions', initialPercentWidth: 20, maxSize: 50 },  // wants 300px, capped at 50
 *   { id: 'name', initialPercentWidth: 50 },                  // wants 750px
 *   { id: 'date', initialPercentWidth: 30 },                  // wants 450px
 * ]);
 * // → { actions: 50, name: ~906, date: ~544 }  // total fills 1500
 * ```
 */
export const calculateColumnWidths = (
  containerWidth: number,
  columns: Array<{
    id?: string;
    initialPercentWidth?: number;
    maxSize?: number;
    minSize?: number;
    size?: number;
  }>,
  fillRemainingSpace = false,
): Record<string, number> => {
  const widths: Record<string, number> = {};

  // First pass: set fixed widths and collect percentage columns
  let totalFixedWidth = 0;
  const percentageColumns: Array<{
    id: string;
    maxSize?: number;
    minSize?: number;
    percentage: number;
  }> = [];

  columns.forEach(({ id, initialPercentWidth, maxSize, minSize, size }) => {
    if (id === undefined) {
      throw new Error('Column ID is required');
    }

    // If has initialPercentWidth, it's a percentage-based column
    if (initialPercentWidth !== undefined) {
      percentageColumns.push({
        id,
        maxSize,
        minSize,
        percentage: initialPercentWidth,
      });
    }
    // Otherwise, use the size as fixed width
    else if (size !== undefined) {
      widths[id] = size;
      totalFixedWidth += size;
    }
  });

  // Second pass: initial allocation respecting min/max
  const availableWidth = containerWidth - totalFixedWidth;

  const allocations = percentageColumns.map(
    ({ id, maxSize, minSize, percentage }) => {
      let width = (availableWidth * percentage) / 100;
      if (minSize !== undefined) {
        width = Math.max(width, minSize);
      }
      if (maxSize !== undefined) {
        width = Math.min(width, maxSize);
      }
      return { id, maxSize, minSize, percentage, width };
    },
  );

  // Third pass: push any leftover width into columns that still have
  // headroom, so maxSize-capped columns don't leave the table short of
  // the container
  const allocated = allocations.reduce((sum, a) => sum + a.width, 0);
  const slack = availableWidth - allocated;
  if (slack > 0) {
    const growable = allocations.filter(
      (a) => a.maxSize === undefined || a.width < a.maxSize,
    );
    const totalPct = growable.reduce((sum, c) => sum + c.percentage, 0);
    if (totalPct > 0) {
      growable.forEach((col) => {
        const share = (slack * col.percentage) / totalPct;
        col.width = Math.min(col.width + share, col.maxSize ?? Infinity);
      });
    }
  }

  // Fourth pass (opt-in): width still unallocated because capped columns hit
  // their maxSize flows into uncapped columns, so the table fills its container
  // with no trailing gap (e.g. after columns are hidden).
  if (fillRemainingSpace) {
    const remaining =
      availableWidth - allocations.reduce((sum, a) => sum + a.width, 0);
    if (remaining > 1) {
      const uncapped = allocations.filter((a) => a.maxSize === undefined);
      const totalUncappedPct = uncapped.reduce(
        (sum, c) => sum + c.percentage,
        0,
      );
      if (totalUncappedPct > 0) {
        uncapped.forEach((col) => {
          col.width += (remaining * col.percentage) / totalUncappedPct;
        });
      } else if (uncapped.length > 0) {
        uncapped.forEach((col) => {
          col.width += remaining / uncapped.length;
        });
      }
    }
  }

  allocations.forEach(({ id, width }) => {
    widths[id] = width;
  });

  return widths;
};

/**
 * Normalizes a spacing value into a [left, right] tuple.
 *
 * This utility function accepts either a single number (applied to both sides)
 * or an explicit [left, right] array, and always returns a [left, right] tuple.
 * Useful for handling flexible margin/padding props that support both formats.
 *
 * @param value - A single number for uniform spacing, or [left, right] tuple for asymmetric spacing
 *
 * @returns A [left, right] tuple of spacing values
 *
 * @example
 * ```typescript
 * getLeftRightValues(24);        // Returns: [24, 24]
 * getLeftRightValues([12, 24]);  // Returns: [12, 24]
 * getLeftRightValues([0, 16]);   // Returns: [0, 16]
 * ```
 */
export const getLeftRightValues = (
  value: number | [number, number],
): [number, number] => {
  if (Array.isArray(value)) {
    return value;
  }
  return [value, value];
};
